'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import {
  createFieldBuilders,
  createCommonSchemas,
  type ValidationMessages,
} from '../utils/validation-i18n';

/**
 * Hook to get localized validation utilities
 * Uses next-intl for message translation
 */
export function useValidationI18n() {
  const t = useTranslations('validation');

  // Create localized messages object
  const messages: ValidationMessages = useMemo(
    () => ({
      required: (field) => (field ? t('fieldRequired', { field }) : t('required')),
      email: () => t('invalidEmail'),
      phone: () => t('invalidPhone'),
      url: () => t('invalidUrl'),
      minLength: (field, min) => t('minLength', { field, min }),
      maxLength: (field, max) => t('maxLength', { field, max }),
      minValue: (field, min) => t('minValue', { field, min }),
      maxValue: (field, max) => t('maxValue', { field, max }),
      invalidFormat: (field) => t('invalidFormat', { field }),
      tcKimlikInvalid: () => t('tcKimlikInvalid'),
      tcKimlikLength: () => t('tcKimlikLength'),
      taxNumberLength: () => t('taxNumberLength'),
      taxNumberFormat: () => t('taxNumberFormat'),
      dateRequired: () => t('dateRequired'),
      invalidDate: () => t('invalidDate'),
      passwordMin: (min) => t('passwordMin', { min }),
      numeric: () => t('numeric'),
      alphanumeric: () => t('alphanumeric'),
      usernameFormat: () => t('usernameFormat'),
      usernameLength: (min, max) => t('usernameLength', { min, max }),
      futureDate: () => t('futureDate'),
      pastDate: () => t('pastDate'),
      dateInRange: (start, end) => t('dateInRange', { start, end }),
      deliveryBeforePickup: () => t('deliveryBeforePickup'),
      customerTypeRequired: () => t('customerTypeRequired'),
      licenseClassRequired: () => t('licenseClassRequired'),
      positiveNumber: () => t('positiveNumber'),
      integerRequired: () => t('integerRequired'),
    }),
    [t]
  );

  // Create localized field builders and schemas
  const fieldBuilders = useMemo(() => createFieldBuilders(messages), [messages]);
  const commonSchemas = useMemo(() => createCommonSchemas(messages), [messages]);

  return {
    messages,
    fieldBuilders,
    commonSchemas,
  };
}
